/* eslint-disable arrow-body-style */
import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useBoolean } from 'ahooks';
import { Icon, Segment } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import { Bubble } from '@home/components/TeaComponents';
import CardSlot from '@gp/components/CardSlot';
import ApprovedTotalDevBudget from './components/ApprovedTotalDevBudget';
import MilestoneApprovedBudgetCost from './components/MilestoneApprovedBudgetCost';
import useCardRightDom from './_hooks/useCardRightDom';
import useDownloadDevBudgetChange from './_hooks/useDownloadDevBudgetChange';
import { FILE_TYPES_MAP } from '@gp/constants/const';

import type { IBaseProps } from '@home/types/common';
import type { SegmentOption } from 'tea-component/lib/segment/SegmentOption';
import type { IMilestone } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import type { ITabType } from './types';
import type { IMilestoneBudgetCostTableInstance } from './components/MilestoneBudgetCostTable';

import './index.scss';

interface IDevBudgetChangeCardProps extends IBaseProps {
  projectId?: string;
  currentMilestone?: IMilestone | null;
}

const EXCEL_FILE_TYPE = Number.parseInt(FILE_TYPES_MAP.EXCEL, 10);

const INITIAL_CATEGORY = '1';

const CATEGORIES: SegmentOption[] = [
  { text: 'Approved Total Development Budget', value: '1' },
  { text: 'Budget Change per Milestone', value: '2' },
];

const DevBudgetChangeCard: React.FC<IDevBudgetChangeCardProps> = (props) => {
  const { style, className, projectId, currentMilestone } = props;

  const tableComp = useRef<IMilestoneBudgetCostTableInstance>(null);

  const [category, setCategory] = useState(INITIAL_CATEGORY);

  const [activeTab, setActiveTab] = useState<ITabType>('discount-chart');

  const [isNoData, setIsNoData] = useState(true);

  const [isExpandHistory, { toggle: toggleIsExpandHistory, setFalse: setFalseIsExpandHistory }] = useBoolean(false);

  const rightDom = useCardRightDom(category, {
    activeTab,
    setActiveTab,
    isNoData,
    isExpandHistory,
    toggleIsExpandHistory: () => {
      if (!isExpandHistory) {
        setTimeout(() => {
          tableComp.current?.scroll2ChangeHistory();
        });
      }
      toggleIsExpandHistory();
    },
  });

  const handleCategoryChange = (payload: string) => {
    if (payload === '1') {
      setFalseIsExpandHistory();
    }
    setIsNoData(true);
    setCategory(payload);
  };

  const { isDownloading, handleDownload } = useDownloadDevBudgetChange(category);

  useEffect(() => {}, []);

  return (
    <CardSlot
      style={style}
      className={classnames('dbg-project-details__finance__dev-budget-change-card', className)}
      title="Budget Evolution by Milestone"
    >
      <CardSlot
        className="dbg-project-details__finance__dev-budget-change-card-inner"
        title={<Segment className="" options={CATEGORIES} value={category} onChange={handleCategoryChange} />}
        rightDom={
          <>
            {rightDom}
            {!isNoData && (
              // @ts-ignore
              <Bubble dark>
                {isDownloading ? (
                  <i className="dbg-card-slot-char">
                    <Icon type="loading" />
                  </i>
                ) : (
                  <i
                    className="dbg-card-slot-char"
                    onClick={() => {
                      handleDownload({
                        project_id: projectId,
                        file_type: EXCEL_FILE_TYPE,
                        fileType: FILE_TYPES_MAP.EXCEL,
                      });
                    }}
                  >
                    <SvgIcon className="dbg-card-slot-charicon" type="download" />
                  </i>
                )}
              </Bubble>
            )}
          </>
        }
      >
        <div className="">
          {category === '1' && (
            <ApprovedTotalDevBudget
              activeTab={activeTab}
              projectId={projectId}
              currentMilestone={currentMilestone}
              setIsNoData={setIsNoData}
            />
          )}
          {category === '2' && (
            <MilestoneApprovedBudgetCost
              tableComp={tableComp}
              projectId={projectId}
              currentMilestone={currentMilestone}
              isExpandHistory={isExpandHistory}
              setIsNoData={setIsNoData}
            />
          )}
        </div>
      </CardSlot>
    </CardSlot>
  );
};

export default DevBudgetChangeCard;
